import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Play, Plus, Power, Trash2 } from "lucide-react";
import {
  createBackupSchedule,
  deleteBackupSchedule,
  listBackupSchedules,
  runBackupScheduleNow,
  updateBackupSchedule,
  type BackupSchedule,
  type BackupScheduleCreate,
  type BackupType,
} from "@/api/endpoints/backupSchedules";
import { getVMs } from "@/api/endpoints/vms";
import { useActiveTenant } from "@/components/tenant/TenantSelector";
import type { VM } from "@/types";

const CRON_PRESETS: { label: string; expr: string }[] = [
  { label: "Every hour", expr: "0 * * * *" },
  { label: "Every 6 hours", expr: "0 */6 * * *" },
  { label: "Daily at 02:00", expr: "0 2 * * *" },
  { label: "Weekly (Sun 03:00)", expr: "0 3 * * 0" },
  { label: "Monthly (1st 04:00)", expr: "0 4 1 * *" },
];

const STATUS_STYLES: Record<string, string> = {
  completed: "bg-emerald-600/20 text-emerald-300",
  running: "bg-sky-600/20 text-sky-300",
  failed: "bg-red-600/20 text-red-300",
  pending: "bg-amber-600/20 text-amber-300",
};

export function BackupSchedules() {
  const qc = useQueryClient();
  const [tenantId] = useActiveTenant();
  const { data: schedules, isLoading } = useQuery({
    queryKey: ["backup-schedules"],
    queryFn: listBackupSchedules,
    refetchInterval: 30_000,
  });
  const { data: vms } = useQuery({
    queryKey: ["vms", tenantId],
    queryFn: () => getVMs(tenantId ?? undefined),
  });
  const vmById = useMemo(() => {
    const m = new Map<number, VM>();
    (vms ?? []).forEach((v) => m.set(v.id, v));
    return m;
  }, [vms]);

  const [open, setOpen] = useState(false);

  const createMut = useMutation({
    mutationFn: createBackupSchedule,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backup-schedules"] });
      setOpen(false);
    },
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      updateBackupSchedule(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["backup-schedules"] }),
  });
  const deleteMut = useMutation({
    mutationFn: deleteBackupSchedule,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["backup-schedules"] }),
  });
  const runNowMut = useMutation({
    mutationFn: runBackupScheduleNow,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backup-schedules"] });
      qc.invalidateQueries({ queryKey: ["backups"] });
    },
  });

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-900">
      <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3">
        <div>
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-blue-400" />
            <h3 className="font-semibold text-gray-100">Scheduled backups</h3>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            APScheduler-driven PBS jobs. A single worker across the cluster
            holds the advisory lock and triggers runs.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          disabled={!vms || vms.length === 0}
          className="flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-500 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" /> New schedule
        </button>
      </div>

      {isLoading ? (
        <div className="p-4 text-sm text-gray-500">Loading schedules…</div>
      ) : !schedules || schedules.length === 0 ? (
        <div className="p-4 text-sm text-gray-500">
          No schedules yet. Add one to trigger recurring backups automatically.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="border-b border-gray-800 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2">VM</th>
              <th className="px-4 py-2">Cron (UTC)</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Last run</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {schedules.map((s) => (
              <ScheduleRow
                key={s.id}
                schedule={s}
                vm={vmById.get(s.vm_id)}
                onToggle={() =>
                  toggleMut.mutate({ id: s.id, enabled: !s.enabled })
                }
                onDelete={() => {
                  if (confirm("Delete this schedule?")) deleteMut.mutate(s.id);
                }}
                onRunNow={() => runNowMut.mutate(s.id)}
                runNowPending={runNowMut.isPending && runNowMut.variables === s.id}
              />
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <CreateScheduleModal
          vms={vms ?? []}
          onClose={() => setOpen(false)}
          onSubmit={(payload) => createMut.mutate(payload)}
          pending={createMut.isPending}
          error={createMut.error ? String(createMut.error) : null}
        />
      )}
    </div>
  );
}

function ScheduleRow({
  schedule,
  vm,
  onToggle,
  onDelete,
  onRunNow,
  runNowPending,
}: {
  schedule: BackupSchedule;
  vm: VM | undefined;
  onToggle: () => void;
  onDelete: () => void;
  onRunNow: () => void;
  runNowPending: boolean;
}) {
  const status = schedule.last_status ?? "never";
  const statusCls = STATUS_STYLES[status] ?? "bg-gray-700 text-gray-300";
  return (
    <tr className={schedule.enabled ? "" : "opacity-60"}>
      <td className="px-4 py-2">
        {vm ? (
          <span className="font-medium text-gray-200">{vm.name}</span>
        ) : (
          <span className="text-gray-500">vm #{schedule.vm_id}</span>
        )}
      </td>
      <td className="px-4 py-2 font-mono text-xs text-gray-300">
        {schedule.cron_expr}
      </td>
      <td className="px-4 py-2 text-xs text-gray-400">{schedule.backup_type}</td>
      <td className="px-4 py-2 text-xs text-gray-400">
        {schedule.last_run_at
          ? new Date(schedule.last_run_at).toLocaleString()
          : <span className="text-gray-600">—</span>}
      </td>
      <td className="px-4 py-2">
        <span className={`rounded px-1.5 py-0.5 text-xs ${statusCls}`}>{status}</span>
      </td>
      <td className="px-4 py-2">
        <div className="flex justify-end gap-2">
          <button
            onClick={onRunNow}
            disabled={!schedule.enabled || runNowPending}
            title={schedule.enabled ? "Run backup now (bypass cron)" : "Enable the schedule first"}
            aria-label="Run backup now"
            className="rounded border border-emerald-700/50 bg-emerald-700/10 px-2 py-0.5 text-xs text-emerald-300 hover:bg-emerald-700/20 disabled:opacity-50"
          >
            <Play className="h-3 w-3" />
          </button>
          <button
            onClick={onToggle}
            title={schedule.enabled ? "Disable" : "Enable"}
            aria-label={schedule.enabled ? "Disable schedule" : "Enable schedule"}
            className="rounded border border-gray-700 bg-gray-800 px-2 py-0.5 text-xs text-gray-300 hover:bg-gray-700"
          >
            <Power className="h-3 w-3" />
          </button>
          <button
            onClick={onDelete}
            aria-label="Delete schedule"
            className="rounded border border-red-700/50 bg-red-700/10 px-2 py-0.5 text-xs text-red-300 hover:bg-red-700/20"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function CreateScheduleModal({
  vms,
  onClose,
  onSubmit,
  pending,
  error,
}: {
  vms: VM[];
  onClose: () => void;
  onSubmit: (p: BackupScheduleCreate) => void;
  pending: boolean;
  error: string | null;
}) {
  const [vmId, setVmId] = useState<number | null>(vms[0]?.id ?? null);
  const [cronExpr, setCronExpr] = useState("0 2 * * *");
  const [backupType, setBackupType] = useState<BackupType>("snapshot");
  const [enabled, setEnabled] = useState(true);

  const canSubmit = vmId !== null && cronExpr.trim().length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-900 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-4 text-lg font-semibold text-gray-100">
          New backup schedule
        </h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit || vmId === null) return;
            onSubmit({
              vm_id: vmId,
              cron_expr: cronExpr,
              backup_type: backupType,
              enabled,
            });
          }}
          className="space-y-3"
        >
          <label className="block text-sm">
            <span className="text-gray-400">Virtual machine</span>
            <select
              required
              value={vmId ?? ""}
              onChange={(e) => setVmId(Number(e.target.value))}
              className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-sm text-gray-100"
            >
              {vms.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} (#{v.vmid} · Tier {v.service_tier})
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="text-gray-400">Cron expression (UTC)</span>
            <input
              required
              value={cronExpr}
              onChange={(e) => setCronExpr(e.target.value)}
              className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-2 py-1.5 font-mono text-sm text-gray-100"
              placeholder="0 2 * * *"
            />
            <div className="mt-2 flex flex-wrap gap-1">
              {CRON_PRESETS.map((p) => (
                <button
                  key={p.expr}
                  type="button"
                  onClick={() => setCronExpr(p.expr)}
                  className={
                    "rounded border px-2 py-0.5 text-xs transition-colors " +
                    (cronExpr === p.expr
                      ? "border-blue-600 bg-blue-600/20 text-blue-200"
                      : "border-gray-700 bg-gray-800 text-gray-400 hover:bg-gray-700")
                  }
                >
                  {p.label}
                </button>
              ))}
            </div>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-gray-400">Type</span>
              <select
                value={backupType}
                onChange={(e) => setBackupType(e.target.value as BackupType)}
                className="mt-1 w-full rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-sm text-gray-100"
              >
                <option value="snapshot">snapshot</option>
                <option value="full">full</option>
                <option value="incremental">incremental</option>
              </select>
            </label>
            <label className="flex items-end gap-2 text-sm text-gray-300 pb-1">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              Enabled on creation
            </label>
          </div>

          {error && <div className="text-sm text-red-400">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-gray-700 bg-gray-800 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || pending}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
            >
              {pending ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
